import React, { useState, useEffect } from 'react';
import { Table, Breadcrumb, Tooltip, message, Upload, Button } from 'antd';
import { UploadOutlined, SaveOutlined, CloseSquareOutlined } from '@ant-design/icons';
import { COOKIE, getCookie } from '../../../../services/cookie';
import constants from '../../../../constants/constants';
import { fetchAdminApi } from '../../../../services/api';
import { getCustomisedBreadcrumbs, success } from '../../../../functions/functions';

const ResourceList = ({ title, fetchUrl, uploadUrl, breadcrumbs, resourceType = 0, uploadSuccessMsg, uploadErrorMsg, selectSuccessMsg, acceptFileType, beforeUpload }) => {
	const [data, setData] = useState([]);
	const [fileList, setFileList] = useState([]);
	const [editableRowIndex, setEditableRowIndex] = useState(null);
	const [messageApi, contextHolder] = message.useMessage();
	const [loadingUploadState, setLoadingUploadState] = useState(false);

	const getList = () => {
		fetchAdminApi(fetchUrl, 'post').then((res) => {
			setData(res?.data || []);
		});
	};

	useEffect(() => {
		getList();
	}, []);

	const handleUpload = (id, resourceId) => {
		setLoadingUploadState(true);
		const formData = new FormData();
		fileList.forEach((file) => formData.append('file', file.originFileObj));
		formData.append('updated_by', 1);
		formData.append('resource_type', resourceType);
		formData.append('chapter_id', id);
		formData.append('details', '');
		if (resourceId) formData.append('resource_id', resourceId);

		fetch(`${constants.AdminApiUrl}${uploadUrl}`, {
			method: 'POST',
			body: formData,
			headers: {
				Authorization: `Bearer ${getCookie(COOKIE.Token)}`
			},
		})
			.then((res) => res.json())
			.then((res) => {
				if (res?.code === 200) {
					setLoadingUploadState(false);
					success('success', uploadSuccessMsg, 'flashSuccess', messageApi);
					getList();
					setEditableRowIndex(null);
					setFileList([]);
				} else {
					setLoadingUploadState(false);
					success('error', uploadErrorMsg, 'flashSuccess', messageApi);
				}
			})
			.catch(() => {
				setLoadingUploadState(false);
				message.error('Upload failed.');
			});
	};

	const handleCustomRequest = (file, index) => {
		setTimeout(() => {
			setEditableRowIndex(index);
			success('success', selectSuccessMsg, 'flashSuccess', messageApi);
		}, 1000);
	};

	const removeFile = () => {
		setEditableRowIndex(null);
		setFileList([]);
	};

	const handleChange = (info) => {
		const newList = [...info.fileList].slice(-1);
		setFileList(newList);
	};

	const columns = [
		{
			title: <div className='text-base'>Chapter</div>,
			dataIndex: "chapter_id",
			key: "chapter_id",
			align: "center",
			width: '7%',
		},
		{
			title: <div className='text-base'>Title</div>,
			dataIndex: "chapter_name",
			key: "chapter_name",
			width: '30%',
		},
		{
			title: <div className='text-base'>File</div>,
			key: "title",
			width: '25%',
			render: (record) => {
				let downloadurl = resourceType !== 0 ? "download-podcast": "download-pdf";
				return <Tooltip title={`Download ${record.title}`}>
					<div
						className='linkColor cursor-pointer'
						onClick={() => {
							fetch(constants.AdminApiUrl + `/${downloadurl}/${record.resource_file_id}`)
								.then((response) => response.blob())
								.then((blob) => {
									const url = URL.createObjectURL(new Blob([blob]));
									const link = document.createElement('a');
									link.href = url;
									link.download = record.title;
									document.body.appendChild(link);
									link.click();
									URL.revokeObjectURL(url);
									link.remove();
								});
						}}
					>
						{record.title}
					</div>
				</Tooltip>
			}
		},
		{
			title: <div className='text-base'>Duration</div>,
			dataIndex: "duration",
			key: "duration",
			width: '8%',
			render: (duration) => (
				<span className="text-sm">{duration}</span>
			),
			hidden: resourceType !== 0 ? false: true,
			align: 'center'
		},
		{
			title: <div className='text-base'>File size</div>,
			dataIndex: "file_size",
			key: "file_size",
			width: '8%',
			render: (fileSize) => (
				<span className="text-sm">{fileSize}</span>
			),
			align: 'center'
		},
		{
			title: <div className='text-base'>Upload date</div>,
			dataIndex: "uploaded_date",
			key: "uploaded_date",
			width: '10%',
			align: 'center'
		},
		{
			title: <div className="text-base">Upload</div>,
			key: "upload",
			align: "center",
			width: '10%',
			render: (_, record, index) => (
				<Upload
					maxCount={1}
					fileList={editableRowIndex === index ? fileList : []}
					customRequest={({ file }) => handleCustomRequest(file, index)}
					accept={acceptFileType}
					onChange={handleChange}
					showUploadList={false}
					onRemove={() => removeFile()}
					beforeUpload={beforeUpload}
				>
					<Button icon={<UploadOutlined />} disabled={editableRowIndex !== null}>Upload</Button>
				</Upload>
			)
		},
		{
			title: <div className='text-base'>Action</div>,
			key: 'action',
			width: '15%',
			align: 'center',
			render: (_, record, index) => (
				<div className='flex'>
					<Button
						type="primary"
						icon={<SaveOutlined />}
						onClick={() => handleUpload(record.chapter_id, record.resource_file_id)}
						disabled={editableRowIndex !== index || fileList.length === 0}
						loading={editableRowIndex !== index || fileList.length === 0 ? false :loadingUploadState}
					>Save</Button>
					<Button
						type="primary"
						className='ml-2'
						icon={<CloseSquareOutlined />}
						onClick={removeFile}
						disabled={editableRowIndex !== index || fileList.length === 0}
					>Cancel</Button>
				</div>
			),
		}
	].filter((val)=> !val.hidden);

	return (
		<div className='p-4 min-h-screen bg-gray-100'>
			<Breadcrumb
				items={getCustomisedBreadcrumbs(constants.flashCardpageCrumbs, breadcrumbs)}
				className='bg-neutral-100 shadow-md p-3 mb-3'
			/>
			{contextHolder}
			<Table
				bordered
				className='!table-fixed'
				columns={columns}
				dataSource={data}
				pagination={false}
				rowKey={(record) => record.chapter_id}
				scroll={{ x: 768 }}
				title={() => <div className="font-bold tracking-wide text-lg pl-3 flashTableTitle">{title}</div>}
			/>
		</div>
	);
};

export default ResourceList
